import { api } from "./api";
import {
  asSaveResponse,
  bodyFor,
  saveFailed,
  saveStarted,
  saveSucceeded,
  shouldFlushNow,
  shouldSaveNow,
  staleNeedsBackoff,
  type Sync,
  type Ticket,
} from "../shared/sync";

const RETRY = [2000, 5000, 15000, 30000];

export interface SaverOpts {
  eventId: string;
  getSync: () => Sync;
  setSync: (s: Sync) => void;
  getTokens: () => { pid: string; editToken: string } | null;
  onUpdate: () => void;
  onError: (err: unknown) => void;
}

export interface SaverDeps {
  request?: typeof api;
  now?: () => number;
  schedule?: (fn: () => void, ms: number) => unknown;
  cancel?: (handle: unknown) => void;
  listen?: (handlers: { onHide: () => void; onFlush: () => void; onOnline: () => void }) => () => void;
}

function listenToPage(handlers: { onHide: () => void; onFlush: () => void; onOnline: () => void }): () => void {
  const onHide = (): void => {
    if (document.visibilityState === "hidden") handlers.onHide();
  };
  document.addEventListener("visibilitychange", onHide);
  window.addEventListener("pagehide", handlers.onFlush);
  window.addEventListener("online", handlers.onOnline);
  return () => {
    document.removeEventListener("visibilitychange", onHide);
    window.removeEventListener("pagehide", handlers.onFlush);
    window.removeEventListener("online", handlers.onOnline);
  };
}

export function startSaver(opts: SaverOpts, deps: SaverDeps = {}): { bump(): void; flush(): void; stop(): void } {
  const request = deps.request ?? api;
  const later = deps.schedule ?? ((fn: () => void, ms: number) => setTimeout(fn, ms));
  const cancel = deps.cancel ?? ((handle: unknown) => clearTimeout(Number(handle)));
  let debounce: unknown = null;
  let retryTimer: unknown = null;
  let retryN = 0;
  /** Whether the most recently settled answer was stale. Reset by any answer that is not stale,
   * including an outright failure: only two stale answers back to back count as real contention. */
  let wasStale = false;
  let stopped = false;

  async function sendWith(started: { sync: Sync; ticket: Ticket }, tokens: { pid: string; editToken: string }): Promise<void> {
    const ticket = started.ticket;
    opts.setSync(started.sync);
    opts.onUpdate();
    const body = bodyFor(started.sync, ticket);
    const encoded = JSON.stringify(body);
    const useKeep = ticket.keepalive && encoded.length <= 60_000;
    try {
      const result = await request("PUT", `/api/events/${opts.eventId}/participants/${tokens.pid}`, {
        body,
        editToken: tokens.editToken,
        keepalive: useKeep,
        parse: asSaveResponse,
      });
      if (stopped) return;
      let s = opts.getSync();
      // A rejoin can reuse the numeric sequence. Only this exact ticket may affect the draft
      // or retry timers; an answer for the old identity has no work left to settle here.
      if (!s.inFlight.includes(ticket)) return;
      if (!result.ok) {
        s = saveFailed(s, ticket, result.status, result.error);
        wasStale = false;
        opts.setSync(s);
        opts.onUpdate();
        // Never retry immediately on "retrying": the backoff exists precisely for that status. A
        // pre-empted failure (an old ticket losing to a newer success or a newer attempt already in
        // flight) can still leave a genuinely newer edit sitting dirty with nothing sending it,
        // since its own debounce already fired and returned while this ticket was in flight.
        if (s.status === "retrying") scheduleRetry();
        else if (shouldSaveNow(s)) void trigger(false).catch(opts.onError);
        return;
      }
      // A single stale answer just means my other tab happened to win a race this time, so resend
      // right away. Two in a row is real contention, worth the same backoff a network failure
      // gets rather than hammering the server. Computed against the PREVIOUS answer, before this
      // one overwrites it below.
      const backoff = staleNeedsBackoff(wasStale, result.data.stale);
      s = saveSucceeded(s, result.data, ticket);
      // This answer's staleness only carries forward while there is still something to send under
      // it: once nothing is left (this edit is fully caught up, or the poll just locked or removed
      // me), the streak is over on its own, so a later, wholly unrelated save must not inherit it.
      // Without this, a stale answer for an edit that never got a further attempt (nothing left to
      // resend) would sit here forever and make the FIRST answer of some future, unrelated edit
      // read as the second stale answer in a row.
      wasStale = result.data.stale && shouldSaveNow(s);
      // Back off only when there is still something to send. A second stale answer for an OLD ticket,
      // with the newest edit already saved, must not leave the status reading "retrying" with
      // nothing to retry (the scheduled send would return at once and nothing would reset it).
      if (backoff && shouldSaveNow(s)) {
        s = { ...s, status: "retrying" };
      }
      opts.setSync(s);
      opts.onUpdate();
      if (s.status === "retrying") {
        scheduleRetry();
        return;
      }
      retryN = 0;
      if (retryTimer !== null) cancel(retryTimer);
      retryTimer = null;
      if (shouldSaveNow(opts.getSync())) {
        void trigger(false).catch(opts.onError);
      }
    } catch (err) {
      // api() does not throw today, but if anything between minting the ticket and settling it
      // ever did, an uncaught throw here would leave this ticket in inFlight forever: the same
      // wedge as never checking tokens before minting one. Settle it as a network failure instead.
      if (!stopped) {
        const current = opts.getSync();
        if (!current.inFlight.includes(ticket)) return;
        const s = saveFailed(current, ticket, 0, "Could not reach the server.");
        wasStale = false;
        opts.setSync(s);
        opts.onUpdate();
        if (s.status === "retrying") scheduleRetry();
      }
      throw err;
    }
  }

  async function trigger(keepalive: boolean): Promise<void> {
    if (stopped) return;
    // Tokens are checked BEFORE minting a ticket: minting one and then bailing out would leave it
    // in inFlight with no request ever sent to settle it, wedging every future save behind it.
    const tokens = opts.getTokens();
    if (!tokens) return;
    const s = opts.getSync();
    if (keepalive ? !shouldFlushNow(s) : !shouldSaveNow(s)) return;
    const started = saveStarted(s, keepalive);
    if (!started) return;
    if (started.ticket.patch && Object.keys(started.ticket.patch).length === 0) return;
    await sendWith(started, tokens);
  }

  function scheduleRetry(): void {
    if (retryTimer !== null) cancel(retryTimer);
    const wait = RETRY[Math.min(retryN, RETRY.length - 1)] ?? 30_000;
    retryN += 1;
    retryTimer = later(() => {
      void trigger(false).catch(opts.onError);
    }, wait);
  }

  function bump(): void {
    if (debounce !== null) cancel(debounce);
    debounce = later(() => {
      debounce = null;
      void trigger(false).catch(opts.onError);
    }, 600);
  }

  /** The tab-close path: sends even while an ordinary ticket for the same draft is in flight,
   * because the browser cancels an ordinary request when the page goes away. Calling this twice in
   * a row (visibilitychange followed by pagehide) sends exactly one request: the second call sees
   * the first call's own keepalive ticket already in flight and `shouldFlushNow` refuses it. */
  function flush(): void {
    void trigger(true).catch(opts.onError);
  }

  function onOnline(): void {
    retryN = 0;
    void trigger(false).catch(opts.onError);
  }

  const unlisten = (deps.listen ?? listenToPage)({ onHide: flush, onFlush: flush, onOnline });

  return {
    bump,
    flush,
    stop() {
      stopped = true;
      if (debounce !== null) cancel(debounce);
      if (retryTimer !== null) cancel(retryTimer);
      unlisten();
    },
  };
}
